import { ShieldAlert, Phone, MapPin, LogOut } from 'lucide-react';
import { useLogout } from '../../../application/hooks/useAuth';
import { Button } from '../ui/Button';
import './AccountClosedScreen.css';

export interface AccountClosedScreenProps {
  fullName?: string;
  userId?: string;
}

/**
 * PRD v1.4.0 §1.3 & §6.2: Permanent Account Closure Screen.
 * Invariant: Closed account cannot be reopened. No reassignment.
 * Permanently blocked with preserved audit history.
 */
export function AccountClosedScreen({ fullName, userId }: AccountClosedScreenProps) {
  const logout = useLogout();

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        window.location.href = '/login';
      },
    });
  };

  return (
    <div className="account-closed-container" data-testid="account-closed-screen">
      <div className="account-closed-card">
        <div className="account-closed-icon-badge">
          <ShieldAlert className="w-12 h-12 text-rose-600" />
        </div>

        <h1 className="account-closed-title">Account Permanently Closed</h1>
        <p className="account-closed-subtitle">
          Account {userId ? `(${userId})` : ''} belonging to <strong>{fullName ?? 'User'}</strong> has been officially closed by Green Valley Administration.
        </p>

        <div className="account-closed-policy-box">
          <h4 style={{ margin: '0 0 8px 0', fontSize: '0.875rem', fontWeight: 600, color: '#991b1b' }}>
            System Integrity & Policy Notice (PRD v1.4.0 §1.3):
          </h4>
          <ul className="account-closed-rules">
            <li>
              <strong>Permanent Closure:</strong> Closed accounts cannot be reopened or restored under any circumstances.
            </li>
            <li>
              <strong>No Ownership Reassignment:</strong> No other person or downline member can claim ownership of this account or apartment subscription.
            </li>
            <li>
              <strong>Preserved Audit Trail:</strong> All prior installments, payment receipts, and commission ledgers remain permanently archived in compliance with real estate regulatory standards.
            </li>
          </ul>
        </div>

        <div className="account-closed-contact">
          <div className="contact-item">
            <Phone size={16} className="text-slate-500" />
            <span>Support Helpline: +880 9612-888999 (Sat–Thu, 9 AM – 6 PM)</span>
          </div>
          <div className="contact-item">
            <MapPin size={16} className="text-slate-500" />
            <span>Head Office: House 42, Road 11, Dhanmondi, Dhaka-1209</span>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={handleLogout}
          data-testid="closed-account-logout-btn"
          style={{ width: '100%', marginTop: 'var(--space-2)' }}
        >
          <LogOut size={16} />
          <span>Exit / Log Out</span>
        </Button>
      </div>
    </div>
  );
}
